import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { NYDUS_NETWORK_API_BASE_URL } from '@rd/core/tokens/rd-lib.token';
import { Observable } from 'rxjs';
import { map, retryWhen } from 'rxjs/operators';
import { DomService } from '../dom/dom.service';
import { NydusNetworkErrorHandlerService } from '../nydus-network-error-handler/nydus-network-error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class NydusNetworkApiDataService {
  constructor(
    private http: HttpClient,
    private errorHandler: NydusNetworkErrorHandlerService,
    private domService: DomService,
    @Inject(NYDUS_NETWORK_API_BASE_URL)
    private nydusNetworkApiBaseUrl: string,
  ) {}

  get<Response>(
    urlSuffix: string,
    params: HttpParams = new HttpParams(),
  ): Observable<Response> {
    return this.http
      .get<Response>(this.buildUrl(urlSuffix), {
        headers: this.buildHeaders(),
        params,
        withCredentials: true,
      })
      .pipe(
        map((res) => res),
        retryWhen((errors$) =>
          this.errorHandler.handleErrorStream(errors$, urlSuffix),
        ),
      );
  }

  getPaginated<Response>(
    urlSuffix: string,
    page: number,
    pageSize: number,
    params: HttpParams = new HttpParams(),
  ): Observable<{ body: Response | null; pageCount: number }> {
    const mergedParams = params
      .set('page[number]', `${page}`)
      .set('page[size]', `${pageSize}`);

    const url = this.buildUrl(urlSuffix);

    return this.http
      .get<Response>(url, {
        headers: this.buildHeaders(),
        observe: 'response',
        params: mergedParams,
        withCredentials: true,
      })
      .pipe(
        map((res) => {
          if (!(res.body as any).meta) {
            throw new Error(`Response doesn't have meta count`);
          }

          const totalCount = (res.body as any).meta.count;

          return {
            body: res.body,
            pageCount: this.calculateTotalPages(totalCount, pageSize),
          };
        }),
        retryWhen((error$) => this.errorHandler.handleErrorStream(error$, url)),
      );
  }

  delete<Response>(
    urlSuffix: string,
    params: HttpParams = new HttpParams(),
  ): Observable<Response> {
    const url = this.buildUrl(urlSuffix);

    return this.http
      .delete<Response>(url, {
        headers: this.buildHeaders(),
        observe: 'body',
        params,
        withCredentials: true,
      })
      .pipe(
        map((res) => res),
        retryWhen((error$) => this.errorHandler.handleErrorStream(error$, url)),
      );
  }

  post<Response>(
    urlSuffix: string,
    body: any,
    params: HttpParams = new HttpParams(),
    headers?: { [key: string]: string | [] },
  ): Observable<Response> {
    const url = this.buildUrl(urlSuffix);

    return this.http
      .post<Response>(url, body, {
        headers: this.buildHeaders(headers),
        observe: 'body',
        params,
        withCredentials: true,
      })
      .pipe(
        map((res) => res),
        retryWhen((error$) => this.errorHandler.handleErrorStream(error$, url)),
      );
  }

  put<Response>(
    urlSuffix: string,
    body: any,
    params: HttpParams = new HttpParams(),
  ): Observable<Response> {
    const url = this.buildUrl(urlSuffix);

    return this.http
      .put<Response>(url, body, {
        headers: this.buildHeaders(),
        observe: 'body',
        params,
        withCredentials: true,
      })
      .pipe(
        map((res) => res),
        retryWhen((error$) => this.errorHandler.handleErrorStream(error$, url)),
      );
  }

  private buildUrl(urlSuffix: string): string {
    return `${this.nydusNetworkApiBaseUrl}/${urlSuffix}`;
  }

  private getAscendaLocaleCookie(): { 'X-Force-Locale': string } | undefined {
    // getCookie return type is undefined string but TS strict: fale won't catch this issue
    // if we pass undefined to headers it will crash the angular http service
    // and cannot send any requests
    const ascendaLocale = this.domService.getCookie('ascenda.locale');
    if (!ascendaLocale) {
      return undefined;
    }
    return { 'X-Force-Locale': ascendaLocale };
  }

  private buildHeaders(options?: { [key: string]: string | [] }): {
    [key: string]: string | [];
  } {
    const headers = {
      'Content-Type': 'application/json',
      ...options,
    };

    const localeCookie = this.getAscendaLocaleCookie();

    return {
      ...headers,
      ...localeCookie,
    };
  }

  private calculateTotalPages(totalCount: number, pageSize: number): number {
    return Math.ceil(totalCount / pageSize);
  }
}
